import { useEffect } from 'react';
import { AnyAction } from 'redux';
import { useDispatch } from 'react-redux';
import { DialogAction } from '@smart-builder/types';

export type PromptDetail = {
  identifier: string;
  promptType: 'dialog' | 'email'; // TODO add more
  abortOnCancel: boolean; // defaults to true

  message?: string;
  dialogType?: string; // TODO DialogType;
  buttons?: string[]; // TODO DialogAction[];
  defaultButton?: string; // TODO DialogAction;
  optionalResult?: string; // TODO DialogAction // if specified, then treated as optional dialog
};

export type PromptRetryCallback = (extraPromptAnswers: any) => void;

export type PromptEventDetail = {
  // Backend specifies the following
  data: PromptDetail;

  // Caller of emitPromptEvent specifies the following
  buildRetryAction?: (answer: any) => AnyAction;
  retryCallback?: PromptRetryCallback;
  onPromptAbort?: () => void; // if abortOnCancel is false, then this is never called.
};

export type PromptHandlerFn = (
  dialog: PromptEventDetail,
) => Promise<DialogAction>; // returns button or answer or null if user cancelled

const promptEventName = 'SB.prompt';
export const emitPromptEvent = (detail: PromptEventDetail): boolean => {
  const event = new CustomEvent(promptEventName, {
    detail,
    bubbles: false, // global document-only event
    cancelable: true, // the default action is to show an error dialog
  });

  // returns whether the default action should run
  return document.dispatchEvent(event);
};

export const usePromptListener = (handler: PromptHandlerFn) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const listener = async (event: CustomEvent) => {
      event.preventDefault();

      const detail = event.detail as PromptEventDetail;
      const { data, onPromptAbort } = detail;
      const { identifier, abortOnCancel } = data;

      const answer = await handler(detail);

      if (answer === DialogAction.Cancel && abortOnCancel) {
        if (onPromptAbort) {
          onPromptAbort();
        }
      } else {
        const wrappedAnswer = { [identifier]: answer };
        if (detail.buildRetryAction) {
          dispatch(detail.buildRetryAction(wrappedAnswer));
        } else {
          detail.retryCallback!(wrappedAnswer);
        }
      }
    };

    document.addEventListener(promptEventName, listener);
    return () => {
      document.removeEventListener(promptEventName, listener);
    };
  }, [dispatch, handler]); // assumes handler is stable (i.e. a useCallback
};
